
// src/App.tsx
import React, { useEffect, useState, useCallback } from 'react'; // Added useState, useCallback
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';
import { useAuth0, LogoutOptions as Auth0LogoutOptions } from '@auth0/auth0-react';
import nestjsxCrudDataProvider, { axiosInstance } from '@refinedev/nestjsx-crud';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useNavigate, useParams } from 'react-router-dom'; // Added useParams
import './App.css';

import { Login } from './pages/login';
import Dashboard from './pages/dashboard';
import { ShiftManagementPage } from './pages/facility-shifts';
import ShiftDetailsPage from './components/shiftDetailPage'; // Original component
import Loading from './components/loading/loading';
import MyProfile from './pages/my-profile';
import {
  Refine,
  AuthProvider,
  Authenticated,
  ErrorComponent,
  useLogout,
  BaseRecord,
  HttpError,
} from '@refinedev/core';
import routerProvider, { NavigateToResource, UnsavedChangesNotifier } from '@refinedev/react-router-v6';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Layout from './components/layout';
import FacilityInfoPage from './pages/facility-info/page';
import TermsOfService from './pages/terms-of-service';
import PrivacyPolicy from './pages/privacy-policy';
import ScrollToTop from './components/ScrollToTop';
dayjs.extend(utc);

// Imports for the views in shift-calendar folder
import ShiftDetailView from './pages/shift-calendar/shift-details';
import WeeklyScheduleView from './pages/shift-calendar/weekly-schedule';
import DayView from './pages/shift-calendar/dayview';

// Import types needed
import { IClinicianProfile, IClinicianShift, IShiftTimes } from './interfaces/types'; // Path to your types.ts
import useApiService from './hooks/useApiService'; // Your API service hook

interface AppUserIdentity extends BaseRecord {
  id: string | number;
  name?: string;
  avatar?: string;
  email?: string;
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

function isHttpError(error: unknown): error is HttpError {
  return typeof error === 'object' && error !== null && 'statusCode' in error;
}

const LogoutComponent = () => {
  const { mutate: refineLogout } = useLogout();
  const navigate = useNavigate();

  useEffect(() => {
    refineLogout(undefined, {
      onSuccess: () => { /* Intentionally empty as Auth0 handles redirect */ },
      onError: (error: HttpError | Error) => {
        navigate('/login');
      },
    });
  }, [refineLogout, navigate]);

  return <Loading />;
};

// Wrapper component to fetch data for ShiftDetailsPage
const ShiftDetailsRouteWrapper: React.FC = () => {
  const { id: shiftIdFromParams } = useParams<{ id: string }>();
  const apiService = useApiService(); // Your custom hook

  const [shift, setShift] = useState<IClinicianShift | undefined>(undefined);
  const [clinicianProfile, setClinicianProfile] = useState<IClinicianProfile | null | undefined>(undefined);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);

  useEffect(() => {
    if (!shiftIdFromParams) {
      setDataError("Shift ID not found in URL.");
      setIsLoadingData(false);
      return;
    }

    const fetchData = async () => {
      setIsLoadingData(true);
      setDataError(null);
      try {
        // 1. Fetch Shift Details (IClinicianShift)
        //    Assuming your API endpoint for a single shift (that matches IClinicianShift structure)
        //    is something like /shifts/{shift_id} or /clinician-shifts/{shift_id}.
        //    Let's use a generic /shifts/details/{shift_id} - YOU MUST VERIFY THIS ENDPOINT.
        const shiftResponse = await apiService.get<IClinicianShift>(`/shifts/${shiftIdFromParams}/details`); //  <<< VERIFY THIS ENDPOINT
        const fetchedShift = shiftResponse.data;
        setShift(fetchedShift);

        // 2. Fetch Clinician Profile (IClinicianProfile)
        //    IClinicianShift has an optional `clinician_id`.
        //    IClinicianProfile is linked by `clinician_id` and `person_id`.
        //    We need to decide which ID to use. If `fetchedShift.clinician_id` is available, that's likely best.
        const clinicianIdToFetch = fetchedShift.clinician_id;

        if (clinicianIdToFetch) {
          // Assuming your API endpoint for a clinician's profile is something like /clinicians/{clinician_id}/profile
          // or /people/{person_id}/profile if you need to use person_id from IClinicianProfile.
          // For now, let's assume we fetch by clinician_id. YOU MUST VERIFY THIS ENDPOINT.
          const profileResponse = await apiService.get<IClinicianProfile>(`/clinicians/clinician-profile?clinicianId=${clinicianIdToFetch}&isFacilityUser=true`);
          setClinicianProfile(profileResponse.data);
        } else {
          setClinicianProfile(null); // Explicitly set to null if no profile can be fetched
        }
      } catch (err) {
        setDataError("Failed to load shift details. Please try again.");
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [shiftIdFromParams, apiService]);

  const handleUpdateShift = useCallback((_id: string, updatedTimes: IShiftTimes) => {
    // This function is called by ShiftDetailsPage after its internal API call succeeds.
    // `updatedTimes` is of type IShiftTimes (i.e., { reported_clock_in?, reported_clock_out? })
    setShift(prevShift => {
      if (!prevShift) return undefined;

      // Update the shift state with the new reported times.
      // The immutable_clock_in/out values are already part of `prevShift`
      // and are used by ShiftDetailsPage when it makes the API call.
      // If the `update-shift-time` API call also returns the immutable times
      // in its response (and thus in IShiftTimes), they would be updated here too.
      // Based on your IShiftTimes, it only contains 'reported_' fields.
      return {
        ...prevShift,
        // If your IShiftTimes *could* include immutable_clock_in/out and you want to update them from there:
        // immutable_clock_in: updatedTimes.immutable_clock_in !== undefined ? updatedTimes.immutable_clock_in : prevShift.immutable_clock_in,
        // immutable_clock_out: updatedTimes.immutable_clock_out !== undefined ? updatedTimes.immutable_clock_out : prevShift.immutable_clock_out,
        // For now, assuming IShiftTimes only reliably provides reported times:
        reported_clock_in: updatedTimes.reported_clock_in, // Add these to IClinicianShift if they should be there
        reported_clock_out: updatedTimes.reported_clock_out, // Add these to IClinicianShift if they should be there
      };
    });
  }, []);


  if (isLoadingData) {
    return <Loading />;
  }

  if (dataError) {
    return <div className="p-6 text-red-500">Error: {dataError}</div>; // Or use ErrorComponent
  }

  // ShiftDetailsPage expects `shift` to be potentially undefined and `clinicianProfile` to be undefined or null
  return (
    <ShiftDetailsPage
      shift={shift}
      clinicianProfile={clinicianProfile}
      onUpdateShift={handleUpdateShift} open={false} handleClose={function (): void {
        throw new Error('Function not implemented.');
      } }    />
  );
};


const App: React.FC = () => {
  const { isLoading: authIsLoading, user, logout: auth0LogoutSdk, getIdTokenClaims } = useAuth0(); // Renamed isLoading to authIsLoading
  const axiosInstanceRef = axiosInstance; // Use Refine's axiosInstance
  const API_URL = import.meta.env.VITE_API_URL;
  const dataProvider = nestjsxCrudDataProvider(API_URL, axiosInstanceRef);

  useEffect(() => {
    const setAuthHeader = async () => {
      try {
        const token = await getIdTokenClaims();
        if (token) {
          axiosInstanceRef.defaults.headers.common = { Authorization: `Bearer ${token.__raw}` };
        }
      } catch (error) {
        // Error getting token for auth header
      }
    };
    // Only attempt to set header if Auth0 is not loading and user is available
    if (!authIsLoading && user) {
        setAuthHeader();
    }
  }, [getIdTokenClaims, axiosInstanceRef, authIsLoading, user]); // Added authIsLoading and user

  if (authIsLoading) {
    return <Loading />;
  }

  const authProvider: AuthProvider = {
    login: async () => ({ success: true }),
    logout: async () => {
      const logoutOptions: Auth0LogoutOptions = {
        returnTo: window.location.origin,
      };
      auth0LogoutSdk(logoutOptions);
      return { success: true };
    },
    onError: async (error: Error) => {
      return { error };
    },
    check: async () => {
      try {
        const token = await getIdTokenClaims();
        if (token) {
          axiosInstanceRef.defaults.headers.common = { Authorization: `Bearer ${token.__raw}` };
          return { authenticated: true };
        }
        return {
          authenticated: false,
          error: { message: 'Check failed', name: 'Token not found' },
          redirectTo: '/login',
        };
      } catch (error) {
        return {
          authenticated: false,
          error: error instanceof Error ? error : new Error(String(error)),
          redirectTo: '/login',
        };
      }
    },
    getPermissions: async () => null,
    getIdentity: async (): Promise<AppUserIdentity | null> => {
      if (user) {
        return {
          id: user.sub || Date.now().toString(),
          name: user.name,
          avatar: user.picture,
          email: user.email,
          ...user,
        };
      }
      return null;
    },
  };

  const currentWeekStart = dayjs().startOf('week').format('YYYY-MM-DD');

  return (
    <BrowserRouter>
      <ScrollToTop />
      <RefineKbarProvider>
        <Refine
          dataProvider={dataProvider}
          routerProvider={routerProvider}
          authProvider={authProvider}
          resources={[
            { name: 'dashboard', list: '/dashboard', meta: { label: 'Dashboard' } },
            {
              name: 'facility-shifts',
              list: '/facility-shifts',
              meta: { label: 'Facility Shifts' },
            },
            {
              name: 'shift-calendar',
              list: `/shift-calendar/week/${currentWeekStart}`,
              meta: { label: 'Shift Calendar' },
            },
            { name: 'my-profile', list: '/my-profile', meta: { label: 'My Profile' } },
            { name: 'shifts', meta: { canDelete: true } }, // Resource for general shift operations
            { name: 'clinicians', meta: {} },             // Resource for clinician operations
            { name: 'people', meta: {} },                  // Resource for people operations
            // You might have specific resources for the detail endpoints if they are structured differently
            // e.g., { name: 'shift-details', list: '/shift-details/:id' }
            // but usually, you fetch specific items using IDs on existing resources.
          ]}
          options={{
            syncWithLocation: true,
            warnWhenUnsavedChanges: true,
            useNewQueryKeys: true,
            projectId: 'SXTD1H-As6Lek-fqTINw',
          }}
        >
          <Routes>
            <Route path="/" element={<Layout />}></Route>
            <Route
              element={
                <Authenticated key="authenticated-routes" fallback={<Navigate to="/login" />}>
                  <Layout />
                </Authenticated>
              }
            >
              <Route index element={<NavigateToResource resource="dashboard" />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/facility-shifts" element={<ShiftManagementPage />} />
              {/* Use the wrapper component here */}
              <Route path="/shift-details/:id" element={<ShiftDetailsRouteWrapper />} />
              <Route path="/shift-calendar">
                <Route index element={<Navigate to={`/shift-calendar/week/${currentWeekStart}`} />} />
                <Route path="week/:dateWeekStart" element={<WeeklyScheduleView />} />
                <Route path="day/:date" element={<DayView />} />
                <Route path="shifts/:shiftId/:openingId" element={<ShiftDetailView />} />

              </Route>
               <Route path="/facility-info" element={<FacilityInfoPage />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />

              <Route path="/my-profile" element={<MyProfile />} />
              <Route path="/logout" element={<LogoutComponent />} />
              <Route path="*" element={<ErrorComponent />} />
            </Route>
            <Route
              element={
                <Authenticated key="auth-pages" fallback={<Outlet />}>
                  <NavigateToResource resource="dashboard" />
                </Authenticated>
              }
            >
              <Route path="/login" element={<Login />} />
            </Route>
            
            {/* Public routes for legal pages */}
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          </Routes>
          <RefineKbar />
          <UnsavedChangesNotifier />
        </Refine>
      </RefineKbarProvider>
    </BrowserRouter>
  );
};

export default App;